import { apiSlice } from './apiSlice'
import { ENDPOINTS } from '../../lib/endpoints'

export interface UserPreferences {
  userId?: string
  theme: string
  onboardingCompleted?: boolean
  termsAgreedAt?: string | null
  interests?: string[]
  followedTags?: string[]
}

export interface UserProfile {
  userId: string
  displayName: string
  bio: string | null
  avatarUrl: string | null
  flair: string | null
}

export interface UserFull {
  id: string
  email?: string        // only present on /users/me (own profile)
  roles: string[]
  createdAt: string
  profile: UserProfile | null
  _count?: { discussions: number; followers: number; following: number }
}

export interface UserSearchResult {
  id: string
  displayName: string
  avatarUrl: string | null
}

export interface AiUsageByModelRow {
  model:            string
  provider:         string
  requestType:      string
  promptTokens:     number
  completionTokens: number
  totalTokens:      number
  requestCount:     number
  costUsd:          number
}

export interface CreatedAgentUsageRow {
  agentId:          string
  agentName:        string
  agentIcon:        string | null
  status:           string
  promptTokens:     number
  completionTokens: number
  totalTokens:      number
  requestCount:     number
  costUsd:          number
}

export interface CreatedAgentUsageResponse {
  userId:  string
  from:    string
  to:      string
  byAgent: CreatedAgentUsageRow[]
}

export interface UserAgentUsageRow {
  agentId:          string
  agentName:        string
  agentIcon:        string | null
  promptTokens:     number
  completionTokens: number
  totalTokens:      number
  requestCount:     number
  costUsd:          number
}

export interface UserAgentUsageResponse {
  userId:  string
  from:    string
  to:      string
  byAgent: UserAgentUsageRow[]
}

export interface AiUsageDateRangeData {
  userId:  string
  from:    string
  to:      string
  totals: {
    promptTokens:     number
    completionTokens: number
    totalTokens:      number
    requestCount:     number
    costUsd:          number
  }
  byModel: AiUsageByModelRow[]
}

export interface AiUsageEntry {
  used: number
  limit: number
  remaining: number
  resetAt: string
}

export interface AiUsageData extends AiUsageEntry {
  tokenBased: boolean
  discussion: AiUsageEntry
  chat: AiUsageEntry
}

export const usersApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAiUsage: builder.query<AiUsageData, void>({
      query: () => ENDPOINTS.users.aiUsage,
      providesTags: ['User'],
    }),
    getAiUsageDateRange: builder.query<AiUsageDateRangeData, { from: string; to: string }>({
      query: ({ from, to }) => ENDPOINTS.users.aiUsageRange(from, to),
      providesTags: ['User'],
    }),
    getAiUsageByAgent: builder.query<UserAgentUsageResponse, { from: string; to: string }>({
      query: ({ from, to }) => ENDPOINTS.users.aiUsageAgents(from, to),
      providesTags: ['User'],
    }),
    getAiUsageByCreatedAgents: builder.query<CreatedAgentUsageResponse, { from: string; to: string }>({
      query: ({ from, to }) => ENDPOINTS.users.aiUsageAgentsCreated(from, to),
      providesTags: ['User'],
    }),
    getUser: builder.query<{ user: UserFull }, string>({
      query: (id) => ENDPOINTS.users.get(id),
      providesTags: (_r, _e, id) => [{ type: 'User', id }],
    }),
    updateProfile: builder.mutation<{ user: UserFull }, { displayName?: string; bio?: string; flair?: string | null }>({
      query: (body) => ({ url: ENDPOINTS.users.updateProfile, method: 'PUT', body }),
      invalidatesTags: ['User'],
    }),
    getPreferences: builder.query<{ preferences: UserPreferences }, void>({
      query: () => ENDPOINTS.users.preferences,
      providesTags: ['User'],
    }),
    updatePreferences: builder.mutation<{ preferences: UserPreferences }, { theme?: string; interests?: string[]; followedTags?: string[]; onboardingCompleted?: boolean }>({
      query: (body) => ({ url: ENDPOINTS.users.preferences, method: 'PUT', body }),
      invalidatesTags: ['User'],
    }),
    followUser: builder.mutation<{ created: boolean }, string>({
      query: (id) => ({ url: ENDPOINTS.users.followUser(id), method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'User', id }, 'User'],
    }),
    unfollowUser: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: ENDPOINTS.users.followUser(id), method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'User', id }, 'User'],
    }),
    getFollowStatus: builder.query<{ isFollowing: boolean }, string>({
      query: (id) => ENDPOINTS.users.followStatus(id),
      providesTags: (_r, _e, id) => [{ type: 'User', id }],
    }),
    getFollowedUsers: builder.query<{
      data: { id: string; roles: string[]; createdAt: string; profile: { displayName: string; avatarUrl: string | null; bio: string | null } | null; followedAt: string }[]
      nextCursor: string | null
      hasMore: boolean
    }, { cursor?: string; limit?: number }>({
      query: (params) => ({ url: ENDPOINTS.users.followingUsers, params }),
      serializeQueryArgs: ({ queryArgs }) => { const { cursor: _c, ...rest } = queryArgs; return rest },
      merge: (current, next, { arg }) => {
        if (!arg.cursor) return next
        return { ...next, data: [...current.data, ...next.data] }
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      providesTags: ['User'],
    }),
    getUserActivity: builder.query<{ activity: Record<string, number>; totalContributions: number }, { id: string; year: number | null }>({
      query: ({ id, year }) => ({ url: ENDPOINTS.users.activity(id), params: year !== null ? { year } : {} }),
      providesTags: (_r, _e, { id }) => [{ type: 'User', id }],
    }),
    getDayActivity: builder.query<{
      date: string
      discussions: { id: string; title: string; commentCount: number; voteScore: number; createdAt: string; categories: { name: string; color: string | null; icon: string | null }[] }[]
      comments: { id: string; content: string; createdAt: string; discussionId: string; discussion: { id: string; title: string } | null }[]
    }, { userId: string; date: string }>({
      query: ({ userId, date }) => ENDPOINTS.users.activityDay(userId, date),
      providesTags: (_r, _e, { userId }) => [{ type: 'User', id: userId }],
    }),
    getUserStats: builder.query<{
      topCategory: { name: string; icon: string | null; color: string | null; slug: string; count: number } | null
      bestDiscussion: { id: string; title: string; voteScore: number; commentCount: number } | null
      aiResponseCount: number
      totalContributions: number
      percentile: number
      totalUsers: number
    }, string>({
      query: (id) => ENDPOINTS.users.stats(id),
      providesTags: (_r, _e, id) => [{ type: 'User', id }],
    }),
    getAiConversations: builder.query<{
      data: {
        id: string; title: string; createdAt: string; commentCount: number; voteScore: number
        categories: { name: string; icon: string | null; color: string | null }[]
        latestAiResponse: { id: string; content: string; createdAt: string; agent: { name: string } | null } | null
      }[]
      nextCursor: string | null
      hasMore: boolean
    }, { userId: string; cursor?: string }>({
      query: ({ userId, cursor }) => ({ url: ENDPOINTS.users.aiConversations(userId), params: cursor ? { cursor } : {} }),
      serializeQueryArgs: ({ queryArgs }) => queryArgs.userId,
      merge: (current, next, { arg }) => {
        if (!arg.cursor) return next
        return { ...next, data: [...current.data, ...next.data] }
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      providesTags: (_r, _e, { userId }) => [{ type: 'User', id: userId }],
    }),
    getSimilarUsers: builder.query<{
      users: { id: string; displayName: string; avatarUrl: string | null; sharedCategories: { name: string; icon: string | null; color: string | null }[] }[]
    }, string>({
      query: (id) => ENDPOINTS.users.similarUsers(id),
      providesTags: (_r, _e, id) => [{ type: 'User', id }],
    }),
    getOnThisDay: builder.query<{
      date: string
      year: number
      discussions: { id: string; title: string; commentCount: number; voteScore: number; createdAt: string; categories: { name: string; icon: string | null; color: string | null }[] }[]
    }, string>({
      query: (id) => ENDPOINTS.users.onThisDay(id),
      providesTags: (_r, _e, id) => [{ type: 'User', id }],
    }),
    getWritingStats: builder.query<{
      stats: { avgWords: number; peakDay: string | null; topWord: string | null; topWordCount: number; totalPosts: number } | null
    }, string>({
      query: (id) => ENDPOINTS.users.writingStats(id),
      providesTags: (_r, _e, id) => [{ type: 'User', id }],
    }),
    getUserFollowers: builder.query<{
      data: { id: string; roles: string[]; createdAt: string; followedAt: string; profile: { displayName: string; avatarUrl: string | null; bio: string | null } | null }[]
      nextCursor: string | null
      hasMore: boolean
    }, { id: string; cursor?: string; search?: string }>({
      query: ({ id, cursor, search }) => ({ url: ENDPOINTS.users.followers(id), params: { limit: 20, ...(cursor ? { cursor } : {}), ...(search ? { search } : {}) } }),
      serializeQueryArgs: ({ queryArgs }) => `followers-${queryArgs.id}-${queryArgs.search ?? ''}`,
      merge: (current, next, { arg }) => {
        if (!arg.cursor) return next
        return { ...next, data: [...current.data, ...next.data] }
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor || currentArg?.search !== previousArg?.search,
      providesTags: (_r, _e, { id }) => [{ type: 'User', id }],
    }),
    getUserFollowing: builder.query<{
      data: { id: string; roles: string[]; createdAt: string; followedAt: string; profile: { displayName: string; avatarUrl: string | null; bio: string | null } | null }[]
      nextCursor: string | null
      hasMore: boolean
    }, { id: string; cursor?: string; search?: string }>({
      query: ({ id, cursor, search }) => ({ url: ENDPOINTS.users.following(id), params: { limit: 20, ...(cursor ? { cursor } : {}), ...(search ? { search } : {}) } }),
      serializeQueryArgs: ({ queryArgs }) => `following-${queryArgs.id}-${queryArgs.search ?? ''}`,
      merge: (current, next, { arg }) => {
        if (!arg.cursor) return next
        return { ...next, data: [...current.data, ...next.data] }
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor || currentArg?.search !== previousArg?.search,
      providesTags: (_r, _e, { id }) => [{ type: 'User', id }],
    }),
  }),
})

export const {
  useGetAiUsageQuery,
  useGetAiUsageDateRangeQuery,
  useGetAiUsageByAgentQuery,
  useGetAiUsageByCreatedAgentsQuery,
  useGetUserQuery,
  useUpdateProfileMutation,
  useGetPreferencesQuery,
  useUpdatePreferencesMutation,
  useFollowUserMutation,
  useUnfollowUserMutation,
  useGetFollowStatusQuery,
  useGetFollowedUsersQuery,
  useGetUserActivityQuery,
  useGetDayActivityQuery,
  useGetUserStatsQuery,
  useGetAiConversationsQuery,
  useGetSimilarUsersQuery,
  useGetOnThisDayQuery,
  useGetWritingStatsQuery,
  useGetUserFollowersQuery,
  useGetUserFollowingQuery,
} = usersApi
